import React, { useEffect, useState } from "react";
import SidebarLayout from "../../../common/sidebar/SidebarLayout";
import { Helmet } from "../../../common/Helmet";
import InfoIcon from "../../../../assets/icons/info.svg";
import Select from "react-select";
import { Bar } from "react-chartjs-2";
import {
  searchClinicsLogic,
  fetchInvoicesFromDB,
  getClinicGroupInvoices,
  fetchHealthCareGroupsSolo,
  downloadRevenueCSV,
} from "../../../../store/features/ops/opsSlice";
import PatientLoader from "../../../common/PatientLoader";
import { useDispatch, useSelector } from "react-redux";
import OPSPrimaryHeader from "../../../common/primaryHeader/OPSPrimaryHeader";
import { InfoCard } from "../../dashboard/DashboardHome";
import { ReactComponent as DownloadIcon } from "../../../../assets/icons/download.svg";
import {
  getLastxMonths,
  getRevenueByMonth,
  getRevenueByMonthCollected,
  getRevenueByMonthOutstanding,
} from "../../../../util/globalFunctions";
import moment from "moment";
import CardLayout from "../../../common/cardLayout/CardLayout";
import { CTooltip } from "@coreui/react";
import RevenueDataTable from "./RevenueDataTable";
import { notify } from "../../../../util/helpers";

const Revenue = () => {
  const { opsLoading, allClinics, healthcareGroups } = useSelector(
    (state) => state.ops
  );
  const [localLoading, setLocalLoading] = useState(true);
  const dateOptions = [
    {
      label: "Past 3 Months",
      value: 3,
    },
    {
      label: "Past 6 Months",
      value: 6,
    },
    {
      label: "Past Year",
      value: 12,
    },
  ];
  const [selectedOption, setSelectedOption] = useState(dateOptions[1]);
  const [selectedRevenueMonthRange, setSelectedRevenueMonthRange] = useState(
    dateOptions[2]
  );

  const [invoiceData, setInvoiceData] = useState([]);

  function getFiltered() {
    const toPlay = allClinics.filter((clinic) =>
      healthcareGroups.find((x) => x.id === clinic.clinic_group)
    );

    return toPlay;
  }

  const customStyles = {
    control: (base, state) => ({
      ...base,
      border: "none",
      boxShadow: state.isFocused ? "none" : "none",
      borderColor: state.isFocused ? "#46beed" : base.borderColor,
      "&:hover": {
        borderColor: state.isFocused ? "#46beed" : base.borderColor,
      },
    }),
  };

  const [yearlyTotal, setYearlyTotal] = useState(0);
  const [monthTotal, setMonthTotal] = useState(0);
  const [invoiceHistoryData, setInvoiceHistoryData] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(true);
  const [healthcareGroupsData, setHealthcareGroupsData] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    getClinicGroupInvoices(selectedRevenueMonthRange?.value || 12)
      .then((res) => {
        setInvoiceHistoryData(res?.data?.data);
        setYearlyTotal(res?.data?.yearly_total);
        setMonthTotal(res?.data?.this_month_revenue);
        setInvoiceLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setInvoiceLoading(false);
      });

    fetchHealthCareGroupsSolo({
      is_test_account: 0,
      search: "",
    })
      .then((res) => {
        setHealthcareGroupsData(res.data);
      })
      .catch((e) => console.log(e));

    dispatch(
      searchClinicsLogic({
        search: "",
      })
    );
    fetchInvoicesFromDB({
      start_date: "1971-02-14",
      end_date: moment().format("YYYY-MM-DD"),
    })
      .then((res) => {
        setInvoiceData(res.data);
        setLocalLoading(false);
      })
      .catch((e) => {
        setLocalLoading(false);
      });
  }, []);
  const labels = getLastxMonths(selectedOption.value);

  const data = {
    labels: labels,
    datasets: [
      {
        label: "Collected",
        backgroundColor: "#1E90FF",
        borderColor: "#1E90FF",
        data: getRevenueByMonth(
          invoiceData,
          getLastxMonths(selectedOption.value),
          getFiltered()
        ),
      },
      {
        label: "Invoiced",
        backgroundColor: "#20B2AA",
        borderColor: "#20B2AA",
        data: getRevenueByMonthCollected(
          invoiceData,
          getLastxMonths(selectedOption.value),
          getFiltered()
        ),
      },
      {
        label: "Outstanding",
        backgroundColor: "#00BFFF",
        borderColor: "#00BFFF",
        data: getRevenueByMonthOutstanding(
          invoiceData,
          getLastxMonths(selectedOption.value),
          getFiltered()
        ),
      },
    ],
  };

  const BarChart = () => {
    return (
      <div className="container-fluid">
        <Bar
          data={data}
          options={{
            legend: {
              labels: {
                usePointStyle: true,
                boxWidth: 6,
              },
            },
            scales: {
              x: {
                gridLines: {
                  display: false,
                },
              },
              y: {
                beginAtZero: true,
                suggestedMax: 999,
                title: {
                  display: true,
                  text: "Revenue",
                  color: "gray",
                  font: {
                    family: "DM Sans",
                    size: 20,
                    style: "normal",
                    lineHeight: 1.2,
                  },
                  padding: { top: 30, left: 0, right: 0, bottom: 0 },
                },

                gridLines: {
                  display: false,
                },
              },
            },
          }}
        />
      </div>
    );
  };

  const [isDownloading, setIsDownloading] = useState(false);
  const downloadCSVData = (months) => {
    if (isDownloading) {
      notify("File generation is in progress, please wait..", "info");
      return;
    }
    notify("Your file will be ready shortly..", "info");
    downloadRevenueCSV(months)
      .then((res) => {
        if (res.status === 200) {
          window.open(res?.data?.url);
        }
        setIsDownloading(false);
      })
      .catch((e) => {
        console.log(e);
        notify("Something went wrong! Try again later!", "error");
        setIsDownloading(false);
      });
  };

  return (
    <SidebarLayout>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Revenue Report | Precise Digital</title>
        <link rel="canonical" href={window.location.origin} />
      </Helmet>
      <div
        className="container-fluid px-3 d-flex justify-content-between"
        style={{ alignContent: "center", alignItems: "center" }}
      >
        <OPSPrimaryHeader
          titleText={`Revenue Report`}
          showSubtitle={false}
          showButton={false}
          showPill={false}
        />
      </div>
      <div
        className="container-fluid px-4 py-2 row"
        style={{
          margin: "0px",
          display: "flex",
        }}
      >
        <div
          className="  col-sm-6 col-md-6 col-lg-6"
          style={{
            margin: "0px",
            padding: "0px",
            border: "5px solid transparent",
            boxSizing: "border-box",
            backgroundClip: "padding-box",
          }}
        >
          <InfoCard
            title={"Total Revenue YTD (Billed/Collected)"}
            stats={`$${Number(yearlyTotal).toLocaleString("en-US")}`}
            information={"Total Revenue YTD (Billed/Collected)"}
            show_link={false}
          />
        </div>
        <div
          className="col-sm-6 col-md-6 col-lg-6"
          style={{
            margin: "0px",
            padding: "0px",
            border: "5px solid transparent",
            boxSizing: "border-box",
            backgroundClip: "padding-box",
          }}
        >
          <InfoCard
            title={"Revenue This Month"}
            stats={`$${Number(monthTotal).toLocaleString("en-US")}`}
            information={"Revenue This Month"}
            show_link={false}
          />
        </div>
      </div>
      <div
        className="col-sm-12 col-md-12 col-lg-12 px-4 py-2"
        style={{
          margin: "0px",
          padding: "0px",
          border: "5px solid transparent",
          boxSizing: "border-box",
          backgroundClip: "padding-box",
        }}
      >
        <CardLayout
          className="container-fluid px-2"
          style={{
            boxShadow: " 0px 5px 10px rgba(80, 110, 131, 0.19)",
          }}
        >
          <div className="d-flex justify-content-between w-100 px-3 pt-3">
            <h5 style={{ textAlign: "left" }}>Revenue Per Month</h5>

            <CTooltip content="Revenue Per Month">
              <img
                style={{ cursor: "pointer" }}
                src={InfoIcon}
                height="15px"
                width="15px"
                alt="This is the total revenue for the current month"
              />
            </CTooltip>
          </div>

          <div className="container-fluid p-3">
            <div className="d-flex w-100">
              <Select
                value={selectedOption}
                onChange={setSelectedOption}
                options={dateOptions}
                styles={customStyles}
              />
            </div>
            <BarChart />
          </div>
        </CardLayout>
      </div>
      <div
        className="col-sm-12 col-md-12 col-lg-12 px-4 py-2"
        style={{
          margin: "0px",
          padding: "0px",
          border: "5px solid transparent",
          boxSizing: "border-box",
          backgroundClip: "padding-box",
        }}
      >
        <CardLayout
          className="container-fluid px-2"
          style={{
            boxShadow: " 0px 5px 10px rgba(80, 110, 131, 0.19)",
          }}
        >
          <div className="d-flex justify-content-between w-100 p-3">
            <h5 style={{ textAlign: "left" }}>Revenue by Healthcare Group</h5>
            <div className="d-flex" style={{ gap: "5px" }}>
              <button
                disabled={isDownloading}
                onClick={() => {
                  downloadCSVData(selectedRevenueMonthRange?.value || 12);
                }}
                className="btn alternative-btn px-3"
              >
                <DownloadIcon /> Download CSV
              </button>
              <Select
                value={selectedRevenueMonthRange}
                onChange={setSelectedRevenueMonthRange}
                options={dateOptions}
                styles={customStyles}
              />
            </div>
          </div>
          {localLoading || opsLoading || invoiceLoading ? (
            <PatientLoader text="" />
          ) : (
            <RevenueDataTable
              months={selectedRevenueMonthRange?.value || 3}
              invoices={invoiceData}
              invoiceHistoryData={invoiceHistoryData}
              healthcareGroupsData={healthcareGroupsData}
              loading={localLoading || opsLoading || invoiceLoading}
            />
          )}
        </CardLayout>
      </div>
    </SidebarLayout>
  );
};
export default Revenue;
